<script setup>
import { ref, watch, computed } from 'vue';
import DatePicker from 'vue-datepicker-next';
import enDatePickerLocale from 'vue-datepicker-next/locale/en';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  scheduledAt: {
    type: String,
    default: null,
  },
  timezone: {
    type: String,
    default: '',
  },
  recurrence: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'confirm', 'clear']);

const { t, locale } = useI18n();

const ptBrDatePickerLocale = {
  formatLocale: {
    months: [
      'Janeiro',
      'Fevereiro',
      'Marco',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ],
    monthsShort: [
      'Jan',
      'Fev',
      'Mar',
      'Abr',
      'Mai',
      'Jun',
      'Jul',
      'Ago',
      'Set',
      'Out',
      'Nov',
      'Dez',
    ],
    weekdays: [
      'Domingo',
      'Segunda-feira',
      'Terca-feira',
      'Quarta-feira',
      'Quinta-feira',
      'Sexta-feira',
      'Sabado',
    ],
    weekdaysShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'],
    weekdaysMin: ['Do', '2a', '3a', '4a', '5a', '6a', 'Sa'],
    firstDayOfWeek: 0,
    firstWeekContainsDate: 6,
  },
  yearFormat: 'YYYY',
  monthFormat: 'MMM',
  monthBeforeYear: true,
};

const localDateTime = ref(null);
const errorMessage = ref('');
const isDatePickerOpen = ref(false);
const recurrenceFrequency = ref(null);
const recurrenceInterval = ref(1);
const recurrenceEndDate = ref(null);
const recurrenceCount = ref(null);
const recurrenceEndType = ref('never'); // 'never', 'date', 'count'
const isRecurrenceEndDatePickerOpen = ref(false);

const recurrenceOptions = [
  {
    value: null,
    label: t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.NONE'),
  },
  {
    value: 'daily',
    label: t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.DAILY'),
  },
  {
    value: 'weekly',
    label: t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.WEEKLY'),
  },
  {
    value: 'monthly',
    label: t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.MONTHLY'),
  },
];

const resolvedTimezone = computed(
  () =>
    props.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
);

const initialize = () => {
  errorMessage.value = '';
  if (props.scheduledAt) {
    const parsed = new Date(props.scheduledAt);
    localDateTime.value = Number.isNaN(parsed.getTime()) ? null : parsed;
  } else {
    localDateTime.value = null;
  }

  // Initialize recurrence values
  if (props.recurrence) {
    recurrenceFrequency.value = props.recurrence.frequency || null;
    recurrenceInterval.value = props.recurrence.interval || 1;

    if (props.recurrence.end_date) {
      recurrenceEndType.value = 'date';
      recurrenceEndDate.value = new Date(props.recurrence.end_date);
    } else if (props.recurrence.count) {
      recurrenceEndType.value = 'count';
      recurrenceCount.value = props.recurrence.count;
    } else {
      recurrenceEndType.value = 'never';
    }
  } else {
    recurrenceFrequency.value = null;
    recurrenceInterval.value = 1;
    recurrenceEndType.value = 'never';
    recurrenceEndDate.value = null;
    recurrenceCount.value = null;
  }
};

watch(
  () => props.show,
  show => {
    if (show) {
      initialize();
    }
  }
);

watch(
  () => props.scheduledAt,
  () => {
    if (!props.show) {
      initialize();
    }
  }
);

const disablePastDates = date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

const handleClose = () => {
  errorMessage.value = '';
  isDatePickerOpen.value = false;
  isRecurrenceEndDatePickerOpen.value = false;
  emit('close');
};

const handleConfirm = () => {
  if (!localDateTime.value) {
    errorMessage.value = t(
      'CONVERSATION.REPLYBOX.SCHEDULE.MODAL.ERROR_REQUIRED'
    );
    return;
  }

  const now = new Date();
  if (localDateTime.value.getTime() <= now.getTime()) {
    errorMessage.value = t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.ERROR_PAST');
    return;
  }

  // Validate recurrence settings
  if (recurrenceFrequency.value) {
    if (recurrenceEndType.value === 'date' && recurrenceEndDate.value) {
      if (recurrenceEndDate.value <= localDateTime.value) {
        errorMessage.value = t(
          'CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.ERROR_END_DATE'
        );
        return;
      }
    }
    if (
      recurrenceEndType.value === 'count' &&
      (!recurrenceCount.value || recurrenceCount.value < 1)
    ) {
      errorMessage.value = t(
        'CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.ERROR_COUNT'
      );
      return;
    }
  }

  const payload = {
    scheduledAt: localDateTime.value.toISOString(),
    timezone: resolvedTimezone.value,
  };

  // Add recurrence data if enabled
  if (recurrenceFrequency.value) {
    payload.recurrence = {
      frequency: recurrenceFrequency.value,
      interval: recurrenceInterval.value,
    };

    if (recurrenceEndType.value === 'date' && recurrenceEndDate.value) {
      payload.recurrence.endDate = recurrenceEndDate.value.toISOString();
    } else if (recurrenceEndType.value === 'count' && recurrenceCount.value) {
      payload.recurrence.count = parseInt(recurrenceCount.value, 10);
    }
  }

  emit('confirm', payload);
  errorMessage.value = '';
};

const handleClear = () => {
  emit('clear');
  errorMessage.value = '';
  isDatePickerOpen.value = false;
  isRecurrenceEndDatePickerOpen.value = false;
};

const closeDatePicker = () => {
  isDatePickerOpen.value = false;
};

const handleDatePickerConfirm = () => {
  isDatePickerOpen.value = false;
};

const closeRecurrenceEndDatePicker = () => {
  isRecurrenceEndDatePickerOpen.value = false;
};

const datePickerLang = computed(() => {
  const currentLocale = locale.value?.toLowerCase() || 'en';
  return currentLocale.startsWith('pt')
    ? ptBrDatePickerLocale
    : enDatePickerLocale;
});

const canClear = computed(() => !!props.scheduledAt);

const isRecurrenceEnabled = computed(() => recurrenceFrequency.value !== null);
</script>

<template>
  <woot-modal
    :show="show"
    :close-on-backdrop-click="false"
    @close="handleClose"
  >
    <woot-modal-header
      :header-title="$t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.TITLE')"
      :header-content="$t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.DESCRIPTION')"
    />
    <div class="px-4 py-4 sm:px-6 sm:py-5">
      <div class="max-h-[68vh] overflow-y-auto pr-1">
        <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div
            class="rounded-lg border border-n-slate-5 p-4 flex flex-col gap-3"
          >
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.PLACEHOLDER') }}
            </label>
            <DatePicker
              v-model:value="localDateTime"
              v-model:open="isDatePickerOpen"
              type="datetime"
              confirm
              :lang="datePickerLang"
              :disabled-date="disablePastDates"
              :append-to-body="false"
              :placeholder="
                $t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.PLACEHOLDER')
              "
              :editable="false"
              :clearable="false"
              input-class="w-full"
              @close="closeDatePicker"
              @confirm="handleDatePickerConfirm"
            />
          </div>

          <!-- Recurrence Section -->
          <div
            class="rounded-lg border border-n-slate-5 p-4 flex flex-col gap-3"
          >
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.LABEL') }}
            </label>
            <select
              v-model="recurrenceFrequency"
              class="w-full px-3 py-2 border border-n-slate-7 rounded-md text-sm bg-n-solid-1"
            >
              <option
                v-for="option in recurrenceOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>

            <div
              class="flex flex-col gap-3 pt-1 pl-3 border-l-2 border-n-slate-6"
              :class="{
                'opacity-60': !isRecurrenceEnabled,
              }"
            >
              <!-- Interval -->
              <div class="flex flex-wrap items-center gap-2">
                <label class="text-sm text-n-slate-11">
                  {{
                    $t(
                      'CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.REPEAT_EVERY'
                    )
                  }}
                </label>
                <input
                  v-model.number="recurrenceInterval"
                  type="number"
                  min="1"
                  :disabled="!isRecurrenceEnabled"
                  class="w-20 px-2 py-1 border border-n-slate-7 rounded text-sm bg-n-solid-1"
                />
                <span class="text-sm text-n-slate-11">
                  {{
                    recurrenceFrequency
                      ? $t(
                          `CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.${recurrenceFrequency.toUpperCase()}_PLURAL`
                        )
                      : ''
                  }}
                </span>
              </div>

              <!-- End Type -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium text-n-slate-12">
                  {{
                    $t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.ENDS')
                  }}
                </label>

                <label class="flex items-center gap-2">
                  <input
                    v-model="recurrenceEndType"
                    type="radio"
                    value="never"
                    class="text-n-primary-11"
                    :disabled="!isRecurrenceEnabled"
                  />
                  <span class="text-sm text-n-slate-11">
                    {{
                      $t(
                        'CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.NEVER'
                      )
                    }}
                  </span>
                </label>

                <label class="flex items-center gap-2">
                  <input
                    v-model="recurrenceEndType"
                    type="radio"
                    value="date"
                    class="text-n-primary-11"
                    :disabled="!isRecurrenceEnabled"
                  />
                  <span class="text-sm text-n-slate-11">
                    {{
                      $t(
                        'CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.ON_DATE'
                      )
                    }}
                  </span>
                </label>

                <DatePicker
                  v-model:value="recurrenceEndDate"
                  v-model:open="isRecurrenceEndDatePickerOpen"
                  type="date"
                  :lang="datePickerLang"
                  :disabled="
                    !isRecurrenceEnabled || recurrenceEndType !== 'date'
                  "
                  :append-to-body="false"
                  :disabled-date="disablePastDates"
                  :placeholder="
                    $t(
                      'CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.SELECT_END_DATE'
                    )
                  "
                  :editable="false"
                  :clearable="false"
                  input-class="w-full"
                  @close="closeRecurrenceEndDatePicker"
                  @change="closeRecurrenceEndDatePicker"
                />

                <label class="flex items-center gap-2">
                  <input
                    v-model="recurrenceEndType"
                    type="radio"
                    value="count"
                    class="text-n-primary-11"
                    :disabled="!isRecurrenceEnabled"
                  />
                  <span class="text-sm text-n-slate-11">
                    {{
                      $t(
                        'CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.AFTER'
                      )
                    }}
                  </span>
                </label>

                <div class="flex flex-wrap items-center gap-2">
                  <input
                    v-model.number="recurrenceCount"
                    type="number"
                    min="1"
                    :disabled="
                      !isRecurrenceEnabled || recurrenceEndType !== 'count'
                    "
                    class="w-20 px-2 py-1 border border-n-slate-7 rounded text-sm bg-n-solid-1"
                  />
                  <span class="text-sm text-n-slate-11">
                    {{
                      $t(
                        'CONVERSATION.REPLYBOX.SCHEDULE.MODAL.RECURRENCE.OCCURRENCES'
                      )
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p v-if="errorMessage" class="mt-3 text-xs text-n-amber-11">
        {{ errorMessage }}
      </p>
      <div class="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <NextButton
          v-if="canClear"
          faded
          slate
          :label="$t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.CLEAR')"
          @click="handleClear"
        />
        <NextButton
          faded
          slate
          :label="$t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.CANCEL')"
          @click="handleClose"
        />
        <NextButton
          :label="$t('CONVERSATION.REPLYBOX.SCHEDULE.MODAL.APPLY')"
          @click="handleConfirm"
        />
      </div>
    </div>
  </woot-modal>
</template>
